import { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames/bind";
import Headroom from "react-headroom";
import noTouchAction from "../../helpers/noTouchAction";
import FacetValues from "./FacetValues";
import DropdownMenu from "./DropdownMenu";
import LargeScreenSort from "../Sort/LargeScreenSort";
import Escapable from "../Escapable";
import TouchOutside from "../TouchOutside";
import WindowEventsListener from "../WindowEventsListener";
import style from "./HorizontalRefinements.css";
import { withTranslation } from "../translation";
import {
  MISC_BAR_HEIGHT,
  CATEGORY_BANNER_ID,
  CHROME_BREADCRUMB_ID,
  SEARCH_TERM_ID
} from "../constants";
import { COLOUR_FACET_ID } from "../../state/modules/facet/constants";
import { getFacetWithSwatches } from "../../client/getFacetWithSwatches";

const combineClasses = classNames.bind(style);

const SORT_ID = "sort";
const SORT_DATA_AUTO_ID = SORT_ID;
const BOTTOM_SPACE = 32;
const styleContext = classNames.bind(style);

const getVerticalDisplacement = element =>
  element
    ? Math.max(
        element.getBoundingClientRect().bottom + BOTTOM_SPACE,
        BOTTOM_SPACE
      )
    : 0;

// if there is a mismatch in the rendering between server
// and client this elements will be replaced by new ones
// making the getPinStart to return 0...
const pinStartElements = (CLIENT
  ? [
      document.getElementById(CHROME_BREADCRUMB_ID),
      document.getElementById(CATEGORY_BANNER_ID),
      document.getElementById(SEARCH_TERM_ID)
    ]
  : []
).filter(elem => !!elem);

const getPinStart = () =>
  pinStartElements.reduce(
    (offset, elem) => offset + Math.max(elem.offsetHeight, elem.clientHeight),
    0
  ) + MISC_BAR_HEIGHT;

export class HorizontalRefinements extends Component {
  constructor(props) {
    super(props);
    this.state = {
      verticalDisplacement: 0
    };
  }

  componentDidMount() {
    const { facets, getColourEnrichments } = this.props;

    if (facets.some(({ id }) => id === COLOUR_FACET_ID)) {
      getColourEnrichments();
    }
  }

  UNSAFE_componentWillReceiveProps() {
    this.updateDisplacement();
  }

  setFacetEl = facetEl => {
    this.facetEl = facetEl;
  };

  getEnrichedFacet(facet) {
    if (facet.id !== COLOUR_FACET_ID) {
      return facet;
    }

    return getFacetWithSwatches({ facet, swatchesMap: this.props.swatchesMap });
  }

  render() {
    const {
      facets,
      newFacets,
      selectAll,
      clear,
      query,
      formatTranslation,
      changeMultiFacet,
      hasFloor
    } = this.props;
    const { openFacetId, verticalDisplacement } = this.state;

    return (
      <Headroom
        className={combineClasses({
          stickyHeader: true,
          stickyHeader_hasFloor: hasFloor
        })}
        disableInlineStyles={true}
        onUnpin={this.focusToggleAndClose}
        pinStart={getPinStart}
        upTolerance={17}
      >
        <div
          role="region"
          aria-label={formatTranslation("generic_accessibility_filters")}
          className={style.container}
        >
          <ul
            data-auto-id="mediumRefinements"
            ref={this.setFacetEl}
            className={style.ul}
          >
            <Escapable escapeHandler={this.focusToggleAndClose} />
            <TouchOutside
              getElement={this.getCurrentDropdownElement}
              outsideHandler={this.toggleOpenFacet}
            />
            <WindowEventsListener
              events={["resize"]}
              handler={this.updateDisplacement}
              toDebounce={true}
            />
            <li
              data-dropdown-id={SORT_ID}
              data-auto-id={SORT_DATA_AUTO_ID}
              className={style.li}
            >
              <DropdownMenu
                id={SORT_ID}
                name={formatTranslation("plp_web_sort_sort")}
                click={this.toggleOpenFacet}
                isOpen={openFacetId === SORT_ID}
                hasSelectedValues={Boolean(query.sort)}
                isNew={false}
                haspopup={"listbox"}
              >
                {openFacetId === SORT_ID && (
                  <LargeScreenSort close={this.focusToggleAndClose} />
                )}
              </DropdownMenu>
            </li>
            {facets.map(facet => (
              <li
                data-dropdown-id={facet.id}
                data-auto-id={facet.id}
                className={style.li}
                key={facet.name}
              >
                <DropdownMenu
                  id={facet.id}
                  name={facet.name}
                  click={this.toggleOpenFacet}
                  isOpen={openFacetId === facet.id}
                  hasSelectedValues={facet.hasSelectedValues}
                  isNew={newFacets.includes(facet.id)}
                  haspopup={true}
                >
                  {openFacetId === facet.id && (
                    <FacetValues
                      facet={this.getEnrichedFacet(facet)}
                      selectAll={selectAll}
                      clear={clear}
                      changeMultiFacet={changeMultiFacet}
                      verticalDisplacement={verticalDisplacement}
                    />
                  )}
                </DropdownMenu>
              </li>
            ))}
          </ul>
        </div>
        <div
          data-block-propagation={true}
          className={styleContext({
            blockInteract: true,
            blockInteract__visible: !!openFacetId
          })}
        />
      </Headroom>
    );
  }

  updateDisplacement = () => {
    const element = this.getCurrentDropdownElement();

    if (!element) {
      return;
    }

    this.setState({
      verticalDisplacement: getVerticalDisplacement(element)
    });
  };

  getCurrentDropdownElement = openFacetId =>
    this.facetEl.querySelector(
      `[data-dropdown-id=${openFacetId || this.state.openFacetId}]`
    );

  toggleOpenFacet = (facetId, facetName) => {
    const currentOpenId = this.state.openFacetId;
    const currentOpenName = this.state.openFacetName;
    const facetToOpenId = facetId !== currentOpenId ? facetId : null;
    const facetToOpenName = facetName !== currentOpenName ? facetName : null;

    facetToOpenId
      ? this.props.analyticsOpenRefinementType({ id: facetId, name: facetName })
      : currentOpenId &&
        this.props.analyticsCloseRefinementType({
          id: currentOpenId,
          name: currentOpenName
        });

    noTouchAction(!!facetToOpenId);

    this.setState({
      openFacetId: facetToOpenId,
      openFacetName: facetToOpenName,
      verticalDisplacement: getVerticalDisplacement(
        this.getCurrentDropdownElement(facetToOpenId)
      )
    });
  };

  focusToggleAndClose = () => {
    this.focusToggle();
    this.toggleOpenFacet();
  };

  focusToggle() {
    const { openFacetId } = this.state;
    const el = this.facetEl.querySelector(
      `[data-dropdown-id="${openFacetId}"] [data-filter-toggle]`
    );

    if (el) {
      el.focus();
    }
  }
}

HorizontalRefinements.propTypes = {
  hasFloor: PropTypes.bool.isRequired,
  facets: PropTypes.array.isRequired,
  newFacets: PropTypes.array.isRequired,
  selectAll: PropTypes.func.isRequired,
  clear: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired,
  changeMultiFacet: PropTypes.func.isRequired,
  analyticsOpenRefinementType: PropTypes.func.isRequired,
  analyticsCloseRefinementType: PropTypes.func.isRequired,
  formatTranslation: PropTypes.func.isRequired,
  swatchesMap: PropTypes.object.isRequired,
  getColourEnrichments: PropTypes.func.isRequired
};

export default withTranslation(HorizontalRefinements);
